import { useState } from "react";
import { Check, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { SocialIcons } from "@/components/SocialIcons";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries/contact", form);
      setDone(true);
      toast.success("Message sent — we'll be in touch soon.");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-navy-900/70 border border-white/10 rounded-xl px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-gold-400/60 transition-colors";

  return (
    <div className="pt-28 pb-24" data-testid="contact-page">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="eyebrow mb-3">Say Hello</p>
          <h1 className="font-serif text-4xl sm:text-6xl text-slate-50">
            Contact <span className="text-gold-gradient">Open Horizon</span>
          </h1>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <Reveal>
            <div className="space-y-8">
              <p className="text-slate-300/85 font-light leading-relaxed max-w-md">
                Questions about releases, licensing, press, or a bespoke song for someone you
                love — write to us directly or use the form. Every message lands in our inbox
                and is answered personally.
              </p>
              <div className="rounded-2xl border border-white/10 bg-navy-900/60 p-7">
                <div className="flex items-center gap-3">
                  <Mail size={18} className="text-gold-400" />
                  <a
                    href="mailto:openhorizonrecords@outlook.com"
                    className="text-gold-200 hover:text-gold-300 transition-colors text-sm"
                    data-testid="contact-email-link"
                  >
                    openhorizonrecords@outlook.com
                  </a>
                </div>
                <div className="mt-6">
                  <p className="text-xs tracking-[0.25em] uppercase text-slate-500 mb-4">Follow the horizon</p>
                  <SocialIcons size={20} />
                </div>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                For bespoke music enquiries with live package pricing, use the{" "}
                <a href="/bespoke" className="text-gold-300 hover:underline">Bespoke Music enquiry form</a>.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            {done ? (
              <div className="rounded-3xl border border-gold-400/25 bg-navy-900/70 p-12 text-center h-full flex flex-col items-center justify-center" data-testid="contact-success">
                <span className="w-14 h-14 rounded-full flex items-center justify-center text-navy-950" style={{ background: "linear-gradient(135deg,#F59E0B,#D4AF37 50%,#B38728)" }}>
                  <Check size={24} />
                </span>
                <h2 className="font-serif text-2xl text-slate-50 mt-6">Message received</h2>
                <p className="text-sm text-slate-400 mt-3 font-light">Thank you — we'll reply as soon as we can.</p>
                <p className="text-xs text-gold-200/90 bg-gold-400/10 border border-gold-400/30 rounded-xl px-4 py-3 mt-5 leading-relaxed">
                  Please check your Spam/Junk folder and mark our reply as safe so it always reaches you.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-3xl border border-gold-400/20 bg-navy-900/70 backdrop-blur-md p-8 sm:p-10 space-y-5" data-testid="contact-form">
                <input required placeholder="Your name" className={inputCls} value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" />
                <input required type="email" placeholder="Email address" className={inputCls} value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" />
                <textarea required rows={6} placeholder="Your message…" className={inputCls} value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message-input" />
                <button type="submit" disabled={sending} className="btn-gold w-full disabled:opacity-50" data-testid="contact-submit-btn">
                  {sending ? "Sending…" : "Send Message"} <Send size={15} />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </div>
  );
}
